import { Component, Inject, PLATFORM_ID, type OnInit } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { catchError, concatMap, EMPTY } from 'rxjs';

import { AccountDetail } from '../../shared/models/account-detail';
import { UserProfile } from '../../shared/models/user-profile';
import { StorageEnum } from '../../shared/enums/storage.enum';
import { Nav } from '../../shared/enums/nav.enum';
import { NotificationType } from '../../shared/enums/notificationtype.enum';
import { NotificationComponent } from '../../shared/components/notification/notification.component';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AlertsService } from '../../shared/services/alerts.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../../shared/services/storage.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { ErrorService } from '../../shared/services/error.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Router, RouterModule } from '@angular/router';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NotificationsService } from '../../shared/services/notifications.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../../shared/services/cms.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../../shared/services/util.service';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { PageHeader } from '../../shared/models/page-header.model';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { SeoService } from '../../shared/services/seo.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AccountService } from '../../shared/services/account.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { BiometricsService } from '../../shared/services/biometrics.service';

@Component({
  selector: 'app-alerts',
  imports: [
    NotificationComponent, //
    MatCheckboxModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    PageHeaderComponent,
    RouterModule,
  ],
  templateUrl: './alerts.component.html',
  styleUrl: './alerts.component.scss',
})
export class AlertsComponent implements OnInit {
  pageReady = false;
  pageLabels: any;
  formErrorMessage = '';
  loginRes: UserProfile = new UserProfile();
  isBrowser = false;
  alertsForm: FormGroup;
  // using deviceId since deviceType lies and says it's moblie
  platform = 'web';
  accountDetail: AccountDetail = new AccountDetail();
  userPhone = '';
  userEmail = '';
  userPhoneType = '';
  userPhoneIsMobile = true;
  headerData = new PageHeader();
  showRenewalAlert = false;
  pageName = 'Alerts Component';
  deviceId = '';

  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) readonly platformId: Object,
    private readonly alerts: AlertsService,
    private readonly bioS: BiometricsService,
    private readonly cms: CmsService,
    private readonly error: ErrorService,
    private readonly fb: FormBuilder,
    private readonly notification: NotificationsService,
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly utils: UtilService,
    private readonly seoS: SeoService,
    private readonly accountS: AccountService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this.loginRes = this.storage.getSession(StorageEnum.LoginResponse);
      this.accountDetail = this.storage.getSession(StorageEnum.AccountDetail);
      this.deviceId = this.bioS.bioInfo.DeviceId;
      this.platform = this.bioS.bioInfo.Platform;
      if (this.accountDetail.CustomerClass?.toUpperCase() !== 'SMLCOM') {
        this.showRenewalAlert = true;
      } else {
        this.showRenewalAlert = false;
      }
      this.alertsForm = this.fb.group({
        BillReady: this.fb.group({
          Email: false,
          Text: false,
          Push: false,
        }),
        PaymentRecieved: this.fb.group({
          Email: true,
          Text: false,
          Push: false,
        }),
        BillDue: this.fb.group({
          Email: false,
          Text: false,
          Push: false,
        }),
        AutopayDraft: this.fb.group({
          Email: false,
          Text: false,
          Push: false,
        }),
        Renewal: this.fb.group({
          Email: true,
          Text: false,
          Push: false,
        }),
      });
    } else {
      this.alertsForm = new FormGroup({});
    }
  }

  ngOnInit(): void {
    this.cms
      .getContent('oas-alerts', 'Alerts')
      .pipe(
        concatMap((data) => {
          this.pageLabels = data;
          this.headerData.Message = this.pageLabels.header;
          this.seoS.setSeoTags(data.seo);

          if (this.isBrowser) {
            this.userEmail =
              this.accountDetail.Person?.EmailAddresses?.find((email) => {
                return email.IsActive && email.IsPrimary;
              })?.Email ?? '';

            const phone =
              this.accountDetail.Person?.PhoneNumbers?.find((phone) => {
                return phone.IsActive && phone.IsPrimary;
              }) ?? null;
            this.userPhone = phone?.Number ?? '';
            this.userPhoneIsMobile = !!this.userPhone && phone?.Type === 0;
            this.userPhoneType = this.accountS.phoneTypeToString(phone?.Type ?? 3);

            if (this.accountDetail.CustomerClass?.toUpperCase() === 'SMLCOM') {
              this.notification.addNotification(
                data.smallComercialAlertNotification,
                NotificationType.Prenotification,
                this.pageName,
              );
            } else {
              this.notification.addNotification(
                data.defaultAlertNotification,
                NotificationType.Prenotification,
                this.pageName,
              );
            }

            const contactLink = this.cms.getDataFromBuilderReference(data.missingContactLinkUrl);
            data.missingContactText += ` <a href="${contactLink.url}" aria-label="${data.missingContactLinkAria}">${data.missingContactLinkText}</a>`;

            if (!this.userEmail && !this.userPhone) {
              data.missingContactText = data.missingContactText
                .replace('{ContactType}', `${data.missingContactEmailContact} or ${data.missingContactPhoneContact}`)
                .replace('{AlertType}', `${data.emailAlertText} and ${data.smsAlertText}`);
              this.notification.addNotification(data.missingContactText, NotificationType.Alert, this.pageName);
            } else if (!this.userEmail) {
              data.missingContactText = data.missingContactText
                .replace('{ContactType}', data.missingContactEmailContact)
                .replace('{AlertType}', data.emailAlertText);
              this.notification.addNotification(data.missingContactText, NotificationType.Alert, this.pageName);
            } else if (!this.userPhone) {
              data.missingContactText = data.missingContactText
                .replace('{ContactType}', data.missingContactPhoneContact)
                .replace('{AlertType}', data.smsAlertText);
              this.notification.addNotification(data.missingContactText, NotificationType.Alert, this.pageName);
            }
            const userId = this.loginRes.UserId;
            const accountNumber = this.loginRes.Accounts[0].AccountNumber;

            return this.alerts.GetUserPreferences(accountNumber, userId);
          } else {
            return EMPTY;
          }
        }),
      )
      .subscribe((data) => {
        data.forEach((alertType) => {
          switch (alertType.NotificationInternalName) {
            case 'BILDN':
              this.alertsForm.get('BillDue')?.patchValue({
                Email: !!alertType.EmailEnabled,
                Text: !!alertType.SMSEnabled,
                Push: !!alertType.PushEnabled,
              });
              break;
            case 'BILRN':
              this.alertsForm.get('BillReady')?.patchValue({
                Email: !!alertType.EmailEnabled,
                Text: !!alertType.SMSEnabled,
                Push: !!alertType.PushEnabled,
              });
              break;
            case 'PAYN':
              this.alertsForm.get('PaymentRecieved')?.patchValue({
                // Email: !!alertType.EmailEnabled,
                Text: !!alertType.SMSEnabled,
                Push: !!alertType.PushEnabled,
              });
              break;
            case 'APAYN':
              this.alertsForm.get('AutopayDraft')?.patchValue({
                Email: !!alertType.EmailEnabled,
                Text: !!alertType.SMSEnabled,
                Push: !!alertType.PushEnabled,
              });
              break;
            case 'RENR':
              this.alertsForm.get('Renewal')?.patchValue({
                // Email: !!alertType.EmailEnabled,
                Text: !!alertType.SMSEnabled,
                Push: !!alertType.PushEnabled,
              });
              break;
            default:
              this.utils.consoleGroup('default alert case: ', alertType.NotificationInternalName);
              break;
          }
        });

        this.pageReady = true;
        this.utils.hideSpinner();
      });
  }

  async savePreferences(): Promise<void> {
    this.utils.showSpinner();
    this.notification.deleteNotification(this.pageLabels.alertSavedNotification);
    const prefData = [];

    const billReady = this.alertsForm.get('BillReady')?.value;
    prefData.push({
      AccountID: this.accountDetail.AccountNumber,
      UserName: this.loginRes.UserName,
      UserId: this.loginRes.UserId,
      NotificationInternalName: 'BILRN',
      EmailEnabled: billReady.Email,
      SMSEnabled: billReady.Text,
      PushEnabled: billReady.Push,
      DeviceId: this.deviceId,
    });

    const payRec = this.alertsForm.get('PaymentRecieved')?.value;
    prefData.push({
      AccountID: this.accountDetail.AccountNumber,
      UserName: this.loginRes.UserName,
      UserId: this.loginRes.UserId,
      NotificationInternalName: 'PAYN',
      EmailEnabled: payRec.Email,
      SMSEnabled: payRec.Text,
      PushEnabled: payRec.Push,
      DeviceId: this.deviceId,
    });
    if (this.accountDetail.IsAutoPay) {
      const autoDraft = this.alertsForm.get('AutopayDraft')?.value;
      prefData.push({
        AccountID: this.accountDetail.AccountNumber,
        UserName: this.loginRes.UserName,
        UserId: this.loginRes.UserId,
        NotificationInternalName: 'APAYN',
        EmailEnabled: autoDraft.Email,
        SMSEnabled: autoDraft.Text,
        PushEnabled: autoDraft.Push,
        DeviceId: this.deviceId,
      });
    } else {
      const billDue = this.alertsForm.get('BillDue')?.value;
      prefData.push({
        AccountID: this.accountDetail.AccountNumber,
        UserName: this.loginRes.UserName,
        UserId: this.loginRes.UserId,
        NotificationInternalName: 'BILDN',
        EmailEnabled: billDue.Email,
        SMSEnabled: billDue.Text,
        PushEnabled: billDue.Push,
        DeviceId: this.deviceId,
      });
    }
    // uncomment when renewal alerts are available
    const renewal = this.alertsForm.get('Renewal')?.value;
    prefData.push({
      AccountID: this.accountDetail.AccountNumber,
      UserName: this.loginRes.UserName,
      UserId: this.loginRes.UserId,
      NotificationInternalName: 'RENR',
      EmailEnabled: this.showRenewalAlert ? renewal.Email : false,
      SMSEnabled: this.showRenewalAlert ? renewal.Text : false,
      PushEnabled: this.showRenewalAlert ? renewal.Push : false,
      DeviceId: this.deviceId,
    });

    // if any push is enabled and not on web, call getDeviceToken
    if (prefData.some((p) => p.PushEnabled) && this.platform !== 'web') {
      await this.alerts.GetDeviceToken();
    }

    this.alerts
      .UpsertPreferences(prefData)
      .pipe(
        catchError((err) => {
          this.utils.consoleGroup('ERROR: Alerts upsertPref: ', err);
          this.error.setErrorData('none', this.pageLabels.saveError);
          void this.router.navigateByUrl(Nav.ErrorPage);
          return EMPTY;
        }),
      )
      .subscribe((data) => {
        this.notification.addNotification(
          this.pageLabels.alertSavedNotification,
          NotificationType.Postnotification,
          this.pageName,
        );
        this.utils.hideSpinner();
      });
  }
}
